import type { ReactNode } from 'react';
import { cn, formatNumber } from '@/lib/utils';
import {
    RefreshCw, Info, Share2, Map, Repeat2, Users, FileText, Sparkles,
    TrendingUp, UserPlus, Fish, Zap,
} from 'lucide-react';
import {
    useProductGrowthLoop,
    kFactorTone,
    gFactorTone,
    pctPointDelta,
    GROWTH_SEGMENT_META,
    SHARE_EVENT_LABELS,
    type ProductPlannerMetrics,
    type ProductViralityMetrics,
    type ProductGrowthHealthMetrics,
    type ProductGrowthSegment,
} from '@/hooks/useProductGrowthLoop';

function fmtPct(v: number | null): string {
    if (v == null) return 'n/d';
    return `${v.toLocaleString('es-ES', { maximumFractionDigits: 1 })}%`;
}

function fmtK(v: number | null): string {
    if (v == null) return 'n/d';
    return v.toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 3 });
}

function fmtRatio(v: number | null): string {
    if (v == null) return 'n/d';
    return v.toLocaleString('es-ES', { minimumFractionDigits: 1, maximumFractionDigits: 2 });
}

function fmtG(v: number | null, lost: number, gained: number): string {
    if (lost === 0 && gained > 0) return '∞';
    if (v == null) return 'n/d';
    return v.toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function fmtNet(n: number): string {
    const sign = n > 0 ? '+' : '';
    return `${sign}${formatNumber(n)}`;
}

function DeltaLine({
    current,
    previous,
    unit = 'pp',
}: {
    current: number | null;
    previous: number | null;
    unit?: 'pp' | 'num';
}) {
    const delta = unit === 'pp'
        ? pctPointDelta(current, previous)
        : (current == null || previous == null ? null : Number((current - previous).toFixed(2)));
    if (delta == null) return null;
    const good = delta >= 0;
    return (
        <span className={cn('text-[11px] font-semibold', good ? 'text-emerald-400' : 'text-rose-400')}>
            {delta > 0 ? '+' : ''}{delta}{unit === 'pp' ? ' pp' : ''} vs periodo ant.
        </span>
    );
}

function StatTile({
    icon,
    title,
    value,
    subtitle,
    delta,
}: {
    icon: ReactNode;
    title: string;
    value: string;
    subtitle: string;
    delta?: React.ReactNode;
}) {
    return (
        <div className="rounded-xl border border-border bg-card p-4 min-w-0">
            <div className="flex items-start justify-between gap-2">
                <p className="text-xs text-muted-foreground">{title}</p>
                <span className="text-primary shrink-0">{icon}</span>
            </div>
            <p className="text-xl font-bold text-foreground mt-1 tabular-nums">{value}</p>
            <p className="text-[11px] text-muted-foreground mt-0.5">{subtitle}</p>
            {delta ? <div className="mt-1">{delta}</div> : null}
        </div>
    );
}

const TONE_BOX = {
    good: 'border-emerald-500/40 bg-emerald-500/10',
    mid: 'border-amber-500/40 bg-amber-500/10',
    low: 'border-border bg-card',
    empty: 'border-border bg-card',
} as const;

const TONE_NUM = {
    good: 'text-emerald-400',
    mid: 'text-amber-400',
    low: 'text-foreground',
    empty: 'text-muted-foreground',
} as const;

function ViralityHero({ v, comparisonLabel }: { v: ProductViralityMetrics; comparisonLabel: string }) {
    const tone = kFactorTone(v.k_factor);
    const hint = {
        good: 'K ≥ 1: el loop viral (invitaciones + enlaces compartidos) sostiene el crecimiento.',
        mid: 'K entre 0,2 y 1: hay viralidad atribuible, pero aún no cubre toda la adquisición.',
        low: v.viral_actions > 0 && (v.attributed_signups === 0)
            ? 'Hay shares, pero casi ningún registro atribuible (código o enlace /c, /m, /u, /invite). K subirá cuando esas llegadas se midan.'
            : 'K < 0,2: el loop viral apenas convierte en registros medibles.',
        empty: 'No hay usuarios activos en el periodo para calcular K.',
    }[tone];

    return (
        <div className={cn('rounded-xl border p-5 space-y-3', TONE_BOX[tone])}>
            <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                    <h3 className="text-sm font-semibold text-foreground">Coeficiente de viralidad (K)</h3>
                    <p className="text-xs text-muted-foreground mt-0.5 max-w-xl">
                        K = i × c: registros atribuibles (invitación o enlace compartido) ÷ activos.
                        i = acciones virales por activo (capturas, perfil, app, club…).
                        {comparisonLabel ? ` ${comparisonLabel}.` : ''}
                    </p>
                </div>
                <Share2 size={18} className="text-primary shrink-0 mt-0.5" />
            </div>
            <div className="flex flex-wrap items-end gap-6">
                <div>
                    <p className="text-[11px] text-muted-foreground mb-1">K</p>
                    <p className={cn('text-4xl font-bold tabular-nums leading-none', TONE_NUM[tone])}>
                        {fmtK(v.k_factor)}
                    </p>
                </div>
                <div>
                    <p className="text-[11px] text-muted-foreground mb-1">i (acciones / activo)</p>
                    <p className="text-2xl font-bold tabular-nums leading-none text-foreground">
                        {fmtK(v.viral_intensity)}
                    </p>
                </div>
                <div>
                    <p className="text-[11px] text-muted-foreground mb-1">c (conversión)</p>
                    <p className="text-2xl font-bold tabular-nums leading-none text-foreground">
                        {fmtPct(v.conversion_rate_pct)}
                    </p>
                </div>
                <div className="text-xs text-muted-foreground space-y-0.5">
                    <p>Periodo ant. K: {fmtK(v.k_factor_previous)}</p>
                    <DeltaLine current={v.k_factor} previous={v.k_factor_previous} unit="num" />
                </div>
            </div>
            <p className="text-[11px] text-muted-foreground">{hint}</p>
        </div>
    );
}

function GrowthHero({ g, comparisonLabel }: { g: ProductGrowthHealthMetrics; comparisonLabel: string }) {
    const tone = gFactorTone(g.coefficient, g.lost, g.gained);
    const hint = {
        good: 'G ≥ 1: ganamos más usuarios activos de los que perdemos (crecimiento neto sano).',
        mid: 'G entre 0,7 y 1: casi reponemos el churn, pero la base no crece con holgura.',
        low: 'G < 0,7: perdemos más activos de los que entran o vuelven. Revisar activación y hábito.',
        empty: g.lost === 0 && g.gained === 0
            ? 'No hay movimiento de activos entre periodos.'
            : 'Sin perdidos en el periodo; el coeficiente no se divide.',
    }[tone];

    return (
        <div className={cn('rounded-xl border p-5 space-y-3', TONE_BOX[tone])}>
            <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                    <h3 className="text-sm font-semibold text-foreground">Coeficiente de crecimiento (G)</h3>
                    <p className="text-xs text-muted-foreground mt-0.5 max-w-xl">
                        Ganados (nuevos + reactivados) ÷ perdidos (sesión el periodo anterior, ninguna ahora).
                        {comparisonLabel ? ` ${comparisonLabel}.` : ''}
                    </p>
                </div>
                <TrendingUp size={18} className="text-primary shrink-0 mt-0.5" />
            </div>
            <div className="flex flex-wrap items-end gap-6">
                <div>
                    <p className="text-[11px] text-muted-foreground mb-1">G</p>
                    <p className={cn('text-4xl font-bold tabular-nums leading-none', TONE_NUM[tone])}>
                        {fmtG(g.coefficient, g.lost, g.gained)}
                    </p>
                </div>
                <div className="text-xs text-muted-foreground space-y-0.5">
                    <p>
                        {formatNumber(g.gained)} ganados
                        {' · '}
                        {formatNumber(g.new_users)} nuevos
                        {' · '}
                        {formatNumber(g.reactivated)} reactivados
                    </p>
                    <p>{formatNumber(g.lost)} perdidos · neto {fmtNet(g.net)}</p>
                    <p>Periodo ant. G: {fmtK(g.coefficient_previous)}</p>
                    <DeltaLine current={g.coefficient} previous={g.coefficient_previous} unit="num" />
                </div>
            </div>
            <p className="text-[11px] text-muted-foreground">{hint}</p>
        </div>
    );
}

function segmentIcon(key: string): ReactNode {
    if (key === 'registered') return <UserPlus size={14} />;
    if (key === 'activated') return <Sparkles size={14} />;
    if (key === 'with_captures') return <Fish size={14} />;
    return <Zap size={14} />;
}

function GrowthSegments({ segments }: { segments: ProductGrowthSegment[] }) {
    if (segments.length === 0) return null;

    return (
        <div className="rounded-xl border border-border bg-card overflow-hidden">
            <div className="px-4 py-3 border-b border-border">
                <h3 className="text-sm font-semibold text-foreground">Salud del crecimiento por segmento</h3>
                <p className="text-[11px] text-muted-foreground mt-0.5">
                    Ganados = entran al segmento en este periodo. Perdidos = ya estaban en el segmento,
                    activos el periodo anterior y sin sesión ahora. G &gt; 1 es saludable.
                </p>
            </div>
            <div className="overflow-x-auto">
                <table className="w-full text-sm">
                    <thead>
                        <tr className="text-[11px] text-muted-foreground border-b border-border">
                            <th className="text-left font-medium px-4 py-2">Segmento</th>
                            <th className="text-right font-medium px-3 py-2">Ganados</th>
                            <th className="text-right font-medium px-3 py-2">Perdidos</th>
                            <th className="text-right font-medium px-3 py-2">Neto</th>
                            <th className="text-right font-medium px-3 py-2">G</th>
                            <th className="text-right font-medium px-4 py-2">Base ant.</th>
                        </tr>
                    </thead>
                    <tbody>
                        {segments.map((seg) => {
                            const meta = GROWTH_SEGMENT_META[seg.key];
                            const tone = gFactorTone(seg.coefficient, seg.lost, seg.gained);
                            return (
                                <tr key={seg.key} className="border-b border-border/60 last:border-0">
                                    <td className="px-4 py-3">
                                        <div className="flex items-start gap-2">
                                            <span className="text-primary mt-0.5">{segmentIcon(seg.key)}</span>
                                            <div>
                                                <p className="font-medium text-foreground">{meta?.label ?? seg.key}</p>
                                                <p className="text-[11px] text-muted-foreground max-w-sm">{meta?.hint}</p>
                                            </div>
                                        </div>
                                    </td>
                                    <td className="text-right tabular-nums px-3 py-3 text-emerald-400">
                                        {formatNumber(seg.gained)}
                                    </td>
                                    <td className="text-right tabular-nums px-3 py-3 text-rose-400">
                                        {formatNumber(seg.lost)}
                                    </td>
                                    <td className={cn(
                                        'text-right tabular-nums px-3 py-3 font-medium',
                                        seg.net >= 0 ? 'text-emerald-400' : 'text-rose-400',
                                    )}>
                                        {fmtNet(seg.net)}
                                    </td>
                                    <td className={cn('text-right tabular-nums px-3 py-3 font-bold', TONE_NUM[tone])}>
                                        {fmtG(seg.coefficient, seg.lost, seg.gained)}
                                    </td>
                                    <td className="text-right tabular-nums px-4 py-3 text-muted-foreground">
                                        {formatNumber(seg.base_previous)}
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

function PlannerBlock({ p }: { p: ProductPlannerMetrics }) {
    return (
        <div className="space-y-3">
            <div>
                <h3 className="text-sm font-semibold text-foreground">Planificar — reportes</h3>
                <p className="text-xs text-muted-foreground mt-0.5 max-w-2xl">
                    Informes de la pestaña Planificar. Descubrimiento = abrieron Planificar.
                    Si no hay regeneraciones Pro en servidor, usamos aperturas de «Reportes generados»
                    (los informes se guardan en el dispositivo).
                    Repetición = volvieron otro día.
                </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                <StatTile
                    icon={<Users size={16} />}
                    title="Usuarios que generan"
                    value={formatNumber(p.report_users)}
                    subtitle={p.report_source === 'regeneraciones'
                        ? `${formatNumber(p.reports)} regeneraciones Pro en el periodo`
                        : `${formatNumber(p.reports)} aperturas de informes en el periodo`}
                    delta={<DeltaLine current={p.report_users} previous={p.report_users_previous} unit="num" />}
                />
                <StatTile
                    icon={<FileText size={16} />}
                    title="Informes por usuario"
                    value={fmtRatio(p.reports_per_user)}
                    subtitle={p.report_source === 'regeneraciones'
                        ? 'Media de regeneraciones entre quienes actualizan'
                        : 'Media de aperturas entre quienes entran a informes'}
                    delta={<DeltaLine current={p.reports_per_user} previous={p.reports_per_user_previous} unit="num" />}
                />
                <StatTile
                    icon={<Map size={16} />}
                    title="Descubrimiento"
                    value={fmtPct(p.discovery_pct)}
                    subtitle={`${formatNumber(p.discovered_users)} de ${formatNumber(p.active_users)} activos abrieron Planificar`}
                    delta={<DeltaLine current={p.discovery_pct} previous={p.discovery_pct_previous} />}
                />
                <StatTile
                    icon={<Repeat2 size={16} />}
                    title="Repiten"
                    value={fmtPct(p.repeat_pct)}
                    subtitle={`${formatNumber(p.repeat_users)} usuarios en 2+ días distintos`}
                    delta={<DeltaLine current={p.repeat_pct} previous={p.repeat_pct_previous} />}
                />
            </div>
            <p className="text-[11px] text-muted-foreground">
                Consultas «Planificar mágicamente» completadas: {formatNumber(p.magico_runs)}
                {p.magico_runs_previous != null ? ` (periodo ant. ${formatNumber(p.magico_runs_previous)})` : ''}.
            </p>
        </div>
    );
}

interface ProductGrowthMetricsPanelProps {
    days: number;
}

export function ProductGrowthMetricsPanel({ days }: ProductGrowthMetricsPanelProps) {
    const { data, isLoading, isError, error, refetch, isFetching } = useProductGrowthLoop(days);

    if (isLoading && !data) {
        return (
            <div className="rounded-xl border border-border bg-card p-8 flex flex-col items-center gap-2">
                <RefreshCw size={20} className="animate-spin text-muted-foreground" />
                <p className="text-xs text-muted-foreground">Calculando viralidad, crecimiento y Planificar…</p>
            </div>
        );
    }

    if (isError) {
        return (
            <div className="rounded-xl border border-rose-500/30 bg-rose-500/5 p-5 space-y-2">
                <p className="text-sm font-semibold text-rose-400">No se pudieron cargar viralidad y crecimiento</p>
                <p className="text-xs text-muted-foreground">{(error as Error)?.message}</p>
                <button
                    type="button"
                    onClick={() => refetch()}
                    className="text-xs text-primary hover:underline inline-flex items-center gap-1"
                >
                    <RefreshCw size={12} className={isFetching ? 'animate-spin' : ''} />
                    Reintentar
                </button>
            </div>
        );
    }

    if (!data) return null;

    const v = data.virality;
    const breakdown = v.share_breakdown.slice(0, 6);
    const attributedPctLabel = v.invite_share_of_signups_pct;

    return (
        <div className="space-y-4">
            <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2 text-[11px] text-muted-foreground">
                    <Info size={14} className="text-primary shrink-0" />
                    <span>Métricas del mismo periodo que el resto de Producto ({days} días).</span>
                </div>
                <button
                    type="button"
                    onClick={() => refetch()}
                    className="p-2 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted/50"
                    title="Actualizar"
                >
                    <RefreshCw size={14} className={isFetching ? 'animate-spin' : ''} />
                </button>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                <ViralityHero v={v} comparisonLabel={data.comparison_label} />
                <GrowthHero g={data.growth} comparisonLabel={data.comparison_label} />
            </div>

            <GrowthSegments segments={data.growth.segments} />

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                <StatTile
                    icon={<Users size={16} />}
                    title="Registros atribuibles"
                    value={formatNumber(v.attributed_signups)}
                    subtitle={`${formatNumber(v.attributed_invites)} por invitación · ${formatNumber(v.attributed_share_links)} por enlace compartido · ${fmtPct(attributedPctLabel)} de ${formatNumber(v.registrations)} altas`}
                    delta={<DeltaLine current={v.attributed_signups} previous={v.attributed_signups_previous} unit="num" />}
                />
                <StatTile
                    icon={<Sparkles size={16} />}
                    title="Invitadores"
                    value={formatNumber(v.inviters)}
                    subtitle={v.invites_per_inviter != null
                        ? `${fmtRatio(v.invites_per_inviter)} amigos por invitador · histórico ${formatNumber(v.invited_users_all_time)}`
                        : `Nadie canjeó un código · histórico ${formatNumber(v.invited_users_all_time)}`}
                    delta={<DeltaLine current={v.inviters} previous={v.inviters_previous} unit="num" />}
                />
                <StatTile
                    icon={<Share2 size={16} />}
                    title="Acciones virales"
                    value={formatNumber(v.viral_actions)}
                    subtitle={`${formatNumber(v.viral_action_users)} usuarios compartieron · ${formatNumber(v.share_captures)} capturas`}
                    delta={<DeltaLine current={v.viral_actions} previous={v.viral_actions_previous} unit="num" />}
                />
                <StatTile
                    icon={<Users size={16} />}
                    title="Llegadas por enlace"
                    value={formatNumber(v.viral_arrivals)}
                    subtitle="first_open con path /c, /m, /u, /invite o /get-app (muchas sin user_id)"
                    delta={<DeltaLine current={v.viral_arrivals} previous={v.viral_arrivals_previous} unit="num" />}
                />
            </div>

            {breakdown.length > 0 ? (
                <div className="flex flex-wrap gap-2">
                    {breakdown.map((row) => (
                        <span
                            key={row.event_type}
                            className="inline-flex items-center gap-1.5 rounded-full border border-border bg-muted/40 px-2.5 py-1 text-[11px] text-muted-foreground"
                        >
                            <span className="text-foreground font-medium">
                                {SHARE_EVENT_LABELS[row.event_type] ?? row.event_type.replace(/^share_/, '')}
                            </span>
                            <span className="tabular-nums">{formatNumber(row.count)}</span>
                        </span>
                    ))}
                </div>
            ) : null}

            <PlannerBlock p={data.planner} />
        </div>
    );
}
